import React, { useState } from 'react';
import { X } from 'lucide-react';

export default function EnhancedBookModal({ book, onClose, onResponse, onEdit, onDelete, onConfirmDelete, isParent }) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleResponse = () => {
    if (selectedOption && onResponse) {
      onResponse(book.id, selectedOption);
    }
    onClose();
  };

  const getMetaTags = () => {
    const tags = [];
    if (book.genre) tags.push(book.genre);
    if (book.isAudiobook) tags.push('Audiobook');
    if (book.estimatedTime) tags.push(book.estimatedTime);
    // Add some smart tags based on content
    if (book.description?.toLowerCase().includes('bestseller')) tags.push('Bestseller');
    return tags;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        
        {/* Violet Gradient Header */}
        <div className="relative bg-gradient-to-br from-violet-500 to-violet-700 text-white p-8">
          {/* Decorative background circle */}
          <div className="absolute -top-12 -right-8 w-48 h-48 bg-white bg-opacity-10 rounded-full"></div>
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex gap-5 relative z-10">
            {/* Book Cover */}
            <div className="flex-shrink-0">
              {book.image ? (
                <img 
                  src={book.image} 
                  alt={book.title}
                  className="w-20 h-30 rounded-lg object-cover shadow-2xl border-2 border-white border-opacity-20"
                />
              ) : (
                <div className="w-20 h-30 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📚</span>
                </div>
              )}
            </div>
            
            {/* Header Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold mb-2 leading-tight">{book.title}</h2>
              {book.author && (
                <p className="text-lg opacity-90 mb-3 font-medium">by {book.author}</p>
              )}
              
              {/* Meta Tags */}
              <div className="flex flex-wrap gap-2">
                {getMetaTags().slice(0, 4).map((tag, index) => (
                  <span
                    key={index}
                    className="bg-white bg-opacity-20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium border border-white border-opacity-20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Why Recommended - show if there's a specific recommendation reason */}
          {(book.whyRecommended || book.why_recommended || book.personalNote) && (
            <div className="bg-violet-50 border-l-4 border-violet-500 rounded-2xl p-5 mb-6">
              <div className="flex items-center gap-2 font-semibold text-violet-600 mb-2 text-sm">
                <span>💭</span>
                <span>Why {book.authorName} recommended this</span>
              </div>
              <p className="text-slate-900 leading-relaxed">
                {book.whyRecommended || book.why_recommended || book.personalNote}
              </p>
            </div>
          )}

          {/* Book Details Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Always show Author if available */}
            {book.author && (
              <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Author</div>
                <div className="font-semibold text-slate-900">{book.author}</div>
              </div>
            )}
            
            {/* Show Genre */}
            {book.genre && (
              <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Genre</div>
                <div className="font-semibold text-slate-900">{book.genre}</div>
              </div>
            )}
            
            {/* Show Reading Time */}
            {book.estimatedTime && (
              <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Reading Time</div>
                <div className="font-semibold text-slate-900">{book.estimatedTime}</div>
              </div>
            )}
            
            {/* Show if it's an audiobook */}
            {book.isAudiobook && (
              <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Format</div>
                <div className="font-semibold text-slate-900">📱 Audiobook</div>
              </div>
            )}
            
            {/* Show Published Year */}
            {book.releaseYear && (
              <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Published</div>
                <div className="font-semibold text-slate-900">{book.releaseYear}</div>
              </div>
            )}
            
            {/* Show Category */}
            {book.category && (
              <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Category</div>
                <div className="font-semibold text-slate-900 capitalize">{book.category}</div>
              </div>
            )}
          </div>

          {/* Description */}
          {book.description && (
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-3 text-slate-900">What it's about</h3>
              <p className={`text-stone-600 leading-relaxed ${
                !descriptionExpanded ? 'line-clamp-4' : ''
              }`}>
                {book.description}
              </p>
              {book.description.length > 200 && (
                <button
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  className="text-violet-500 font-semibold mt-2 hover:text-violet-600 transition-colors"
                >
                  {descriptionExpanded ? 'Read less' : 'Read more'}
                </button>
              )}
            </div>
          )}

          {/* Engagement Section */}
          {!isParent && (
            <div className="bg-stone-50 rounded-2xl p-5 mb-6">
              <h3 className="font-semibold text-lg text-center mb-4 text-slate-900">
                How do you want to engage?
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { key: 'reading-list', label: 'Add to Reading List', icon: '📚', desc: 'Plan to read this' },
                  { key: 'reading-challenge', label: 'Add to Challenge', icon: '🎯', desc: 'Count for your goal' },
                  { key: 'start-reading', label: 'Start Reading', icon: '⚡', desc: 'Get it now' },
                  { key: 'learn-more', label: 'Learn More', icon: '🔍', desc: 'Reviews & samples' },
                  { key: 'not-interested', label: 'Not Interested', icon: '✋', desc: 'Different genres?' }
                ].map((option) => (
                  <button
                    key={option.key}
                    onClick={() => handleOptionSelect(option.key)}
                    className={`p-4 rounded-xl border-2 transition-all text-center relative ${
                      selectedOption === option.key
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-stone-200 hover:border-stone-400 bg-white'
                    }`}
                  >
                    {selectedOption === option.key && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        ✓
                      </div>
                    )}
                    <div className="text-2xl mb-2">{option.icon}</div>
                    <div className="font-semibold text-sm mb-1 text-slate-900">{option.label}</div>
                    <div className="text-xs text-stone-500">{option.desc}</div>
                  </button>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <a
                  href={book.links?.[0] || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-stone-100 hover:bg-stone-200 border border-stone-200 rounded-lg py-3 text-center text-stone-700 font-medium text-sm transition-colors"
                >
                  Preview
                </a>
                <a
                  href={book.links?.[0] || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-stone-100 hover:bg-stone-200 border border-stone-200 rounded-lg py-3 text-center text-stone-700 font-medium text-sm transition-colors"
                >
                  Reviews
                </a>
                <a
                  href={book.links?.[0] || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white rounded-lg py-3 text-center font-medium text-sm transition-colors"
                >
                  Get Book
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Recommendation Meta */}
        <div className="flex items-center gap-3 p-5 bg-stone-50 border-t border-stone-200">
          <div className="w-9 h-9 bg-teal-500 rounded-full flex items-center justify-center text-white font-semibold">
            {book.authorName?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm">Recommended by {book.authorName}</div>
            <div className="text-xs text-stone-500">
              {book.createdAt ? new Date(book.createdAt).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              }) : 'Recently'}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-5 border-t border-stone-200 bg-white">
          {isParent ? (
            <>
              <button
                onClick={() => {
                  onClose();
                  onConfirmDelete ? onConfirmDelete(book.id) : onDelete && onDelete(book);
                }}
                className="flex-1 px-4 py-3 border border-stone-200 text-red-600 font-semibold rounded-xl hover:bg-red-50 transition-colors"
              >
                Remove
              </button>
              <button
                onClick={() => onEdit && onEdit(book)}
                className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 transition-colors"
              >
                Edit
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  onClose();
                  onConfirmDelete ? onConfirmDelete(book.id) : onDelete && onDelete(book);
                }}
                className="flex-1 px-4 py-3 border border-red-200 text-red-600 font-semibold rounded-xl hover:bg-red-50 transition-colors"
              >
                Remove
              </button>
              <button
                onClick={() => onEdit && onEdit(book)}
                className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  onResponse && onResponse(book.id, 'add_to_big3');
                  onClose();
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-violet-500 to-violet-600 text-white font-semibold rounded-xl hover:from-violet-600 hover:to-violet-700 transition-colors"
              >
                🎯 Add to Big3
              </button>
              <button
                onClick={handleResponse}
                disabled={!selectedOption}
                className="flex-1 px-4 py-3 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Send Response
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}